/** Matches the notebook service's HTTPS-only media contract; URLs are never fetched by our server. */
export function notebookMediaUrl(value: unknown): string | undefined {
  if (
    typeof value !== "string" ||
    value.length > 2000 ||
    value !== value.trim() ||
    /[\u0000-\u0020\\]/u.test(value) ||
    !/^https:\/\//iu.test(value)
  ) {
    return undefined
  }
  try {
    const url = new URL(value)
    const host = url.hostname.toLowerCase().replace(/\.$/u, "")
    const local =
      !host.includes(".") ||
      host.includes(":") ||
      /(?:^|\.)\d+$/u.test(host) ||
      /(?:^|\.)(?:localhost|local|internal|lan|home|home\.arpa)$/u.test(host)
    return !local && !url.username && !url.password ? value : undefined
  } catch {
    return undefined
  }
}

/** Common watch/embed pages cannot be loaded by a native video element. */
export function notebookVideoUrl(value: unknown): string | undefined {
  const src = notebookMediaUrl(value)
  if (!src) {
    return undefined
  }
  const hostname = new URL(src).hostname.toLowerCase().replace(/\.$/u, "")
  const watchDomains = [
    "youtube.com",
    "youtube-nocookie.com",
    "youtu.be",
    "vimeo.com",
  ]
  return watchDomains.some(
    (domain) => hostname === domain || hostname.endsWith(`.${domain}`),
  )
    ? undefined
    : src
}

/** Printed documents cannot play video; source links remain usable in exported PDFs. */
export function replaceNotebookVideosForExport(root: HTMLElement) {
  root.querySelectorAll("video").forEach((video) => {
    const figure = video.ownerDocument.createElement("figure")
    const label = video.getAttribute("aria-label") || "Video"
    const poster = notebookMediaUrl(video.getAttribute("poster"))
    if (poster) {
      const image = video.ownerDocument.createElement("img")
      image.src = poster
      image.alt = `${label} — video preview`
      image.style.width = video.style.width || "100%"
      image.referrerPolicy = "no-referrer"
      figure.append(image)
    }
    const caption = video.ownerDocument.createElement("p")
    caption.textContent = `${label} (video). `
    const src = notebookMediaUrl(video.getAttribute("src"))
    if (src) {
      const link = video.ownerDocument.createElement("a")
      link.href = src
      link.textContent = "Play original video"
      caption.append(link)
    }
    figure.append(caption)
    video.replaceWith(figure)
  })
}
